import React, { useState } from 'react';
import './Playlist.css';

// Use empty string for same-origin requests (production), or override with REACT_APP_API_URL
const API_URL = process.env.REACT_APP_API_URL || '';

function Playlist({ onSaveHistory }) {
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [playlistData, setPlaylistData] = useState(null);
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [downloadingTracks, setDownloadingTracks] = useState(new Set());
  const [progress, setProgress] = useState({ current: 0, total: 0, active: false });
  const [currentPage, setCurrentPage] = useState(1);
  const tracksPerPage = 10;

  const showStatus = (msg, type) => {
    setStatus({ message: msg, type });
    if (type === 'success' || type === 'error') {
      setTimeout(() => setStatus(null), 4500);
    }
  };

  const handleExtractPlaylist = async (e) => {
    e.preventDefault();

    if (!playlistUrl.trim()) {
      showStatus('Please enter a playlist URL', 'error');
      return;
    }

    const youtubePlaylistRegex = /youtube\.com.*[?&]list=/;
    if (!youtubePlaylistRegex.test(playlistUrl)) {
      showStatus('Please enter a valid YouTube playlist URL', 'error');
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    showStatus('Extracting playlist...', 'loading');

    try {
      const response = await fetch(`${API_URL}/playlist/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: playlistUrl.trim() }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to extract playlist');
      }

      const data = await response.json();

      if (data.requires_manual) {
        showStatus(
          'Note: Spotify playlists require manual search. Use the Search tab for each song.',
          'info'
        );
      } else {
        showStatus(`✓ Successfully loaded ${data.total_tracks} tracks!`, 'success');
      }

      // Initialize all tracks as deselected (user can select all if needed)
      setSelectedTracks(new Set());
      setPlaylistData(data);
    } catch (error) {
      setPlaylistData(null);
      showStatus(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleTrackSelection = (index) => {
    const newSelection = new Set(selectedTracks);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedTracks(newSelection);
  };

  const toggleAllTracks = () => {
    if (selectedTracks.size === playlistData.tracks.length) {
      setSelectedTracks(new Set());
    } else {
      setSelectedTracks(new Set(playlistData.tracks.map((_, idx) => idx)));
    }
  };

  const handleDownloadTrack = async (trackIndex) => {
    const track = playlistData.tracks[trackIndex];

    if (!track.url) {
      showStatus('Invalid track URL', 'error');
      return;
    }

    setDownloadingTracks((prev) => new Set([...prev, trackIndex]));
    showStatus(`Downloading: ${track.title}...`, 'loading');

    try {
      const url = `${API_URL}/download?url=${encodeURIComponent(track.url)}${
        track.cover ? `&cover=${encodeURIComponent(track.cover)}` :
        (track.thumbnail ? `&cover=${encodeURIComponent(track.thumbnail)}` : '')
      }`;

      const response = await fetch(url);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }

      const blob = await response.blob();
      const filename = `${track.title || 'audio'}.mp3`;

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      showStatus(`✓ Downloaded: ${track.title}`, 'success');

      // Save to history if available
      if (typeof onSaveHistory === 'function') {
        const newItem = {
          title: track.title,
          artist: track.artist || track.uploader || '',
          url: track.url,
          cover: track.cover || track.thumbnail || '',
          duration: track.duration,
          timestamp: Date.now(),
        };
        onSaveHistory(newItem);
      }
    } catch (error) {
      showStatus(`Error downloading ${track.title}: ${error.message}`, 'error');
    } finally {
      setDownloadingTracks((prev) => {
        const next = new Set(prev);
        next.delete(trackIndex);
        return next;
      });
    }
  };

  // Request browser notification permission
  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const sendNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedTracks.size === 0) {
      showStatus('Please select at least one track', 'error');
      return;
    }

    const tracksToDownload = Array.from(selectedTracks).map((idx) =>
      playlistData.tracks[idx]
    );


    setLoading(true);
    setProgress({ current: 0, total: tracksToDownload.length, active: true });
    showStatus(`Starting downloads for ${tracksToDownload.length} track(s)...`, 'loading');
    requestNotificationPermission();

    try {
      // Download all selected tracks
      for (let i = 0; i < tracksToDownload.length; i++) {
        const track = tracksToDownload[i];
        const trackIndex = playlistData.tracks.indexOf(track);
        await handleDownloadTrack(trackIndex);
        setProgress((prev) => ({ ...prev, current: i + 1 }));
        // Add a delay between downloads
        if (i < tracksToDownload.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }

      showStatus('✓ All downloads complete!', 'success');
      setProgress((prev) => ({ ...prev, active: false }));
      sendNotification('Playlist Download Complete', 'All selected tracks have been downloaded.');
    } catch (error) {
      showStatus(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
      setProgress((prev) => ({ ...prev, active: false }));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Pagination with smart page numbers (1 2 ... 9 10 format)
  const totalPages = playlistData ? Math.ceil(playlistData.tracks.length / tracksPerPage) : 0;
  const startIndex = (currentPage - 1) * tracksPerPage;
  const endIndex = startIndex + tracksPerPage;
  const currentTracks = playlistData ? playlistData.tracks.slice(startIndex, endIndex) : [];

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return [...Array(totalPages)].map((_, i) => i + 1);
    }

    const pages = new Set([1, 2, totalPages - 1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const validPages = [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < validPages.length; i++) {
      const page = validPages[i];
      if (i > 0 && page - validPages[i - 1] > 1) {
        result.push('...');
      }
      result.push(page);
    }
    return result;
  };

  const goToPage = (page) => {
    if (typeof page === 'number') {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="playlist-container">
      <div className="playlist-form-section">
        <form className="playlist-form" onSubmit={handleExtractPlaylist}>
          {status && <Status message={status.message} type={status.type} />}

          <div className="input-group">
            <label htmlFor="playlistUrl">YouTube Playlist Link</label>
            <input
              id="playlistUrl"
              type="text"
              value={playlistUrl}
              onChange={(e) => setPlaylistUrl(e.target.value)}
              placeholder="https://www.youtube.com/playlist?list=..."
              disabled={loading}
            />
          </div>

          <div className="action-buttons">
            <button type="submit" className="btn btn-primary" disabled={loading || !playlistUrl.trim()}>
              {loading && playlistData === null ? (
                <>
                  <span className="loader"></span>
                  Extracting...
                </>
              ) : (
                'Extract Playlist'
              )}
            </button>
            {playlistData && !playlistData.requires_manual && (
              <button
                type="button"
                className="btn btn-download"
                onClick={handleDownloadSelected}
                disabled={loading || selectedTracks.size === 0}
              >
                {loading ? (
                  <>
                    <span className="loader"></span>
                    Downloading...
                  </>
                ) : (
                  `⬇ Download (${selectedTracks.size})`
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {playlistData && !playlistData.requires_manual && (
        <div className="playlist-section">
          {progress.active && progress.total > 0 && (
            <div className="playlist-progress-bar">
              <div className="playlist-progress" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
              <span className="playlist-progress-label">
                Downloaded {progress.current} of {progress.total}
              </span>
            </div>
          )}
          <div className="playlist-header">
            <h2>{playlistData.playlist_title}</h2>
            <p className="playlist-info">
              {playlistData.total_tracks} track{playlistData.total_tracks !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="selection-controls">
            <button
              className="btn btn-secondary"
              onClick={toggleAllTracks}
            >
              {selectedTracks.size === playlistData.tracks.length
                ? 'Deselect All'
                : 'Select All'}
            </button>
            <span className="selection-count">
              {selectedTracks.size} of {playlistData.tracks.length} selected
            </span>
          </div>

          <div className="tracks-list">
            {playlistData.tracks.length === 0 ? (
              <p className="no-tracks">No tracks found in this playlist</p>
            ) : (
              currentTracks.map((track) => {
                const index = playlistData.tracks.indexOf(track);
                const isSelected = selectedTracks.has(index);

                return (
                  <div 
                    key={index} 
                    className={`track-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleTrackSelection(index)}
                  >
                    <div className="track-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTrackSelection(index)}
                        onClick={(e) => e.stopPropagation()}
                        id={`track-${index}`}
                      />
                    </div>

                    {(track.cover || track.thumbnail) && (
                      <div className="track-thumbnail">
                        <img src={track.cover || track.thumbnail} alt={track.title} />
                      </div>
                    )}

                    <div className="track-info">
                      <div className="meta">
                        <h3 className="track-title">{track.title || 'Unknown title'}</h3>
                        <div className="track-artist">{track.artist || track.uploader || 'Unknown artist'}</div>
                        {track.album && <div className="track-album">{track.album}</div>}
                        {track.duration > 0 && (
                          <div className="track-duration">{formatDuration(track.duration)}</div>
                        )}
                        {track.year && <div className="track-year">{track.year}</div>}
                        {downloadingTracks.has(index) && progress.active && (
                          <span className="track-progress-spinner" title="Downloading..." />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary btn-pagination"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`btn-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>
              <button
                className="btn btn-secondary btn-pagination"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {playlistData && playlistData.requires_manual && (
        <div className="info-box">
          <p>{playlistData.message}</p>
        </div>
      )}
    </div>
  );
}

function Status({ message, type }) {
  return <div className={`status status-${type}`}>{message}</div>;
}

export default Playlist;
